import React from "react"
import { Tooltip } from "antd"
import { QuestionCircleOutlined } from "@ant-design/icons"
// @ts-ignore
import { Badge, TextInput } from "components-design-system"
import type { ActivityTypeKey, ActivityStatus } from "../LinkActivitiesModal"
import "./ActivityListItem.scss"

type ActivitySatusBadgeProps = {
  status: string
  isUnlinked?: boolean
  className?: string
}

export const ActivityStatusBadge = ({ status, isUnlinked = false, className }: ActivitySatusBadgeProps) => {
  let colorName = ""
  if (isUnlinked) {
    colorName = "gray"
  } else if (status === "New") {
    colorName = "blue"
  } else if (status === "In Progress") {
    colorName = "yellow"
  } else if (status === "Completed") {
    colorName = "green"
  } else if (status === "Cancelled") {
    colorName = "red"
  }
  return (
    <Badge
      colorName={colorName}
      value={status}
      className={className}
    />
  )
}

type ActivityFormListItemProps = {
  type: ActivityTypeKey
  id: number
  title?: string
  status: ActivityStatus
  onRemove?: (id: number, type: ActivityTypeKey) => void
  name?: string | null // "create" and "attach"
  onSetName?: (name: string | null, id: number, type: ActivityTypeKey) => void
  isLinked?: boolean // "linked"
  onLink?: (id: number, shouldLink: boolean) => void // "linked"
  isCompleted?: boolean // "create"
  onComplete?: (shouldComplete: boolean, id: number) => void
  disabled?: boolean
}

// constants

const NAME_TOOLTIP =
  "The Activity Name is set by default to the selected Activity Type. Edit the field to set a custom Activity Name for this assessment."

const ActivityListItem = ({
  type,
  id,
  title,
  status,
  onRemove,
  name,
  onSetName,
  isLinked,
  onLink,
  isCompleted,
  onComplete,
  disabled,
}: ActivityFormListItemProps) => {
  const [isEditing, setIsEditing] = React.useState(false)
  const [savedName, setSavedName] = React.useState<string | null>()
  const inputRef = React.useRef<HTMLInputElement>(null)

  // save current name when open modal
  React.useEffect(() => {
    setSavedName(name)
  }, [])

  // focus on input when open edit panel
  React.useEffect(() => {
    if (isEditing && inputRef?.current?.focus) inputRef.current.focus()
  }, [isEditing])

  const isUnlinked = type === "linked" && !isLinked

  let actions = null
  // LINK/UNLINK
  if (type === "linked") {
    actions = [
      <Tooltip
        key="link"
        title={isLinked ? "Unlink" : "Link"}
      >
        <i
          className={isLinked ? "fa-regular fa-link-slash" : "fal fa-link"}
          onClick={onLink ? () => onLink(id, !isLinked) : undefined}
        />
      </Tooltip>,
    ]
  } else {
    actions = [
      // REMOVE
      <Tooltip
        key="remove"
        title="Remove"
      >
        <i
          className="fal fa-times-circle"
          onClick={onRemove ? () => onRemove(id, type) : undefined}
        />
      </Tooltip>,
    ]
    // MARK COMPLETED
    if (type === "new") {
      actions.push(
        <Tooltip
          key="new"
          title="Mark completed"
        >
          <i
            className={`${isCompleted ? "fas" : "fal"} fa-check-circle`}
            onClick={onComplete ? () => onComplete(!isCompleted, id) : undefined}
          />
        </Tooltip>
      )
    }
    // EDIT
    actions.push(
      <Tooltip
        key="edit"
        title="Edit custom name"
      >
        <i
          className="fa-light fa-pen-to-square"
          onClick={() => {
            setIsEditing((prev) => !prev)
          }}
        />
      </Tooltip>
    )
  }

  return (
    <div
      className={`activity-list-item-wrapper${isUnlinked ? " list-item-unlinked" : ""}${
        disabled ? " list-item-disabled" : ""
      }`}
    >
      <Tooltip
        title={
          isUnlinked
            ? "Activity will be unlinked from the assessment. If the assessment that the activity is being unlinked from is the one that set its due date, then the due date of the activity will be set to a manual due date. This date can be set or another linked assessment to set the due date can be selected from the Edit Steps and Due Date modal for the activity."
            : null
        }
      >
        <div className="list-item">
          <div className="list-item-content">
            <div className="list-item-header">
              <span className="list-item-title">{title}</span>
              {name && <span className="list-item-name"> ({name})</span>}
            </div>
            <ActivityStatusBadge
              status={status}
              isUnlinked={isUnlinked}
              className="activity-list-item-badge"
            />
          </div>

          {/* EDITING NAME */}
          {isEditing && (
            <div className="list-item-edit">
              <div className="list-item-edit__label">
                Custom activity name
                <Tooltip title={NAME_TOOLTIP}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </div>
              <div className="list-item-edit-form">
                <TextInput
                  ref={inputRef}
                  defaultValue={name}
                  size="small"
                  onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                    onSetName && onSetName(evt.target.value || null, id, type)
                  }}
                  allowClear
                />
                <Tooltip title="Cancel renaming">
                  <i
                    className="fas fa-times-circle"
                    onClick={() => {
                      if (onSetName) onSetName(savedName || null, id, type)
                      setIsEditing(false)
                    }}
                  />
                </Tooltip>
              </div>
            </div>
          )}
        </div>
      </Tooltip>
      <div className="list-item-actions">{actions}</div>
    </div>
  )
}

export default ActivityListItem
